import React, { useEffect, useMemo, useState } from 'react'
import {
  Add,
  Delete,
  Save,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { useInspectionChecklist, InspectionChecklistKind } from '@/hooks/repair/useInspectionChecklist'
import { InspectionChecklistQuestion } from '@otw/models'

interface RepairShopChecklistDialogProps {
  open: boolean
  shopName?: string
  checklistId?: string
  /** Label for dialog title: Intake or Complete. */
  checklistKindLabel?: string
  checklistKind?: InspectionChecklistKind
  isSaving?: boolean
  onClose: () => void
  onSave: (questions: InspectionChecklistQuestion[]) => Promise<void>
}

const createQuestion = (order: number): InspectionChecklistQuestion => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  question: '',
  enabled: true,
  allowNotes: true,
  order,
  answerOptions: ['yes', 'no', 'na'],
})

const normalizeQuestions = (questions: InspectionChecklistQuestion[]): InspectionChecklistQuestion[] =>
  questions.map((question, index) => ({
    ...question,
    order: index,
    answerOptions: ['yes', 'no', 'na'],
    allowNotes: true,
  }))

const RepairShopChecklistDialog: React.FC<RepairShopChecklistDialogProps> = ({
  open,
  shopName,
  checklistId,
  checklistKindLabel = 'Inspection',
  checklistKind = 'complete',
  isSaving = false,
  onClose,
  onSave,
}) => {
  const { showError } = useSnackbar()
  const { questions: loadedQuestions, isLoading, error } = useInspectionChecklist({
    checklistId,
    kind: checklistKind,
    fallbackToDefault: true,
  })
  const [draftQuestions, setDraftQuestions] = useState<InspectionChecklistQuestion[]>([])
  const [pendingDeleteQuestion, setPendingDeleteQuestion] = useState<InspectionChecklistQuestion | null>(null)

  useEffect(() => {
    if (!open) return
    setDraftQuestions(normalizeQuestions(loadedQuestions))
  }, [open, loadedQuestions])

  const hasChanges = useMemo(() => {
    return JSON.stringify(normalizeQuestions(draftQuestions)) !== JSON.stringify(normalizeQuestions(loadedQuestions))
  }, [loadedQuestions, draftQuestions])

  const handleAddQuestion = () => {
    setDraftQuestions((prev) => [...prev, createQuestion(prev.length)])
  }

  const handleDeleteQuestion = (questionId: string) => {
    setDraftQuestions((prev) => normalizeQuestions(prev.filter((question) => question.id !== questionId)))
  }

  const handleQuestionChange = (questionId: string, value: string) => {
    setDraftQuestions((prev) =>
      prev.map((question) => (question.id === questionId ? { ...question, question: value } : question))
    )
  }

  const handleEnabledChange = (questionId: string, enabled: boolean) => {
    setDraftQuestions((prev) =>
      prev.map((question) => (question.id === questionId ? { ...question, enabled } : question))
    )
  }

  const handleSave = async () => {
    const normalized = normalizeQuestions(draftQuestions)
    if (normalized.some((question) => !question.question.trim())) {
      showError('Please fill out all question text before saving.')
      return
    }
    await onSave(normalized)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {shopName
          ? `${shopName} ${checklistKindLabel} Checklist`
          : `Repair Shop ${checklistKindLabel} Checklist`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>


          {(error || !loadedQuestions) && (
            <Alert severity="warning">
              {error || 'Checklist could not be loaded. You can still create questions manually.'}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : draftQuestions.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No checklist questions yet. Add your first question to continue.
              </Typography>
            </Paper>
          ) : (
            draftQuestions.map((question, index) => (
              <Paper key={question.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2">Question {index + 1}</Typography>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => setPendingDeleteQuestion(question)}
                      aria-label={`Delete question ${index + 1}`}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>

                  <TextField
                    fullWidth
                    label="Question"
                    value={question.question}
                    onChange={(event) => handleQuestionChange(question.id, event.target.value)}
                    placeholder="Example: Are there any visible fluid leaks?"
                  />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label="Yes" size="small" color="success" variant="outlined" />
                    <Chip label="No" size="small" color="error" variant="outlined" />
                    <Chip label="N/A" size="small" color="default" variant="outlined" />
                    <Chip label="Optional notes" size="small" color="info" variant="outlined" />
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={question.enabled}
                        onChange={(event) => handleEnabledChange(question.id, event.target.checked)}
                      />
                    }
                    label={question.enabled ? 'Enabled' : 'Disabled'}
                  />
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button variant="outlined" startIcon={<Add />} onClick={handleAddQuestion}>
          Add Question
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={isSaving || isLoading || !hasChanges}
          >
            {isSaving ? 'Saving...' : 'Save Checklist'}
          </Button>
        </Stack>
      </DialogActions>
      <ConfirmDialog
        open={pendingDeleteQuestion != null}
        title="Delete checklist question?"
        message={`Are you sure you want to delete "${
          pendingDeleteQuestion?.question?.trim() || 'this question'
        }"? This action cannot be undone.`}
        confirmText="Delete question"
        cancelText="Cancel"
        severity="error"
        onCancel={() => setPendingDeleteQuestion(null)}
        onConfirm={() => {
          if (!pendingDeleteQuestion) return
          handleDeleteQuestion(pendingDeleteQuestion.id)
          setPendingDeleteQuestion(null)
        }}
      />
    </Dialog>
  )
}

export default RepairShopChecklistDialog
